/**
 * Faction look for model instances: replaces MAT_* slots with the shared library materials, bakes the per-object
 * shader data (sockets, seed, panel class, the look's packed livery) and attaches engine, light and shield effects at sockets
 * (contract §2.7, §8.2). Asset-lane owned (task A5 owns this file; engine.ts, lights.ts and shield.ts belong to A11).
 * Greybox geometry without an ECL_Mask edge bake gets a fallback edge field once per source geometry (edgeBake.ts).
 * Every ShipLook setter is allocation-free and safe to call every frame (D-014).
 */
import { Matrix4, type BufferGeometry, type Mesh } from 'three/webgpu'
import type { ModelInstance } from '../../assets/loader'
import { MATERIAL_SLOTS, type MaterialSlot } from '../../data/assetSchema'
import type { FactionId } from '../contracts'
import { attachEngineFx, type PooledPlume } from './engine'
import type { Variant } from './hull/context'
import { EDGE_DIST_ATTR, bakeEdgeField, geometryTraits, type GeometryTraits } from './hull/edgeBake'
import { LIVERY_KEY } from './hull/livery'
import { HULL_DATA_KEY, buildHullObjectData, dataForMesh, type HullObjectData } from './hull/objectData'
import { internals, type MaterialLibrary } from './library'
import { attachShipLights, createStaticLightPool, type ShipLights } from './lights'
import { createLookState, LOOK_STATE_KEY, recordHit, type ShipLookState } from './lookState'
import { attachShieldFx } from './shield'

export interface ShipLook {
  /** main and retro 0..1 (actual thrust output fractions); boost lengthens and brightens the main plumes. */
  setThrust(main: number, retro: number, boost: boolean): void
  /** Ship-local sway/heave thrust and pitch/yaw/roll torque fractions, -1..1, for ENG_RCS_* puffs. */
  setControl(sway: number, heave: number, pitch: number, yaw: number, roll: number): void
  /** 1 = pristine, 0 = destroyed. Drives scorch and glowing cracks. */
  setDamage(hullFraction: number): void
  /** Hull impact in asset space (metres) with strength 0..1: scorch mark near the hit. */
  hit(localX: number, localY: number, localZ: number, strength: number): void
  /** Shield impact in asset space with strength 0..1: ripple over the hull silhouette. */
  shieldHit(localX: number, localY: number, localZ: number, strength: number): void
  /** The plumes a plume pool draws (`ShipLookOptions.pooledPlumes`), else empty (PERF L-M4 part 3). */
  readonly plumes: readonly PooledPlume[]
  dispose(): void
}

export function slotOf(name: string): MaterialSlot | null {
  if (!name.startsWith('MAT_')) return null
  const slot = name.slice(4)
  return (MATERIAL_SLOTS as readonly string[]).includes(slot) ? (slot as MaterialSlot) : null
}

interface PreparedGeometry {
  readonly traits: GeometryTraits
  /** The geometry the hull slot renders (the source, or its fallback edge-field copy). */
  readonly hull: BufferGeometry
}

const prepared = new WeakMap<BufferGeometry, PreparedGeometry>()

/** Mask/UV traits of a model geometry and, for unbaked hull geometry, its edge-field copy (cached per source). */
export function prepareGeometry(geometry: BufferGeometry, featureAngleDeg: number): PreparedGeometry {
  let p = prepared.get(geometry)
  if (p) return p
  const traits = geometryTraits(geometry)
  const hull = traits.baked || geometry.getAttribute(EDGE_DIST_ATTR) ? geometry : bakeEdgeField(geometry, featureAngleDeg)
  p = { traits, hull }
  prepared.set(geometry, p)
  if (hull !== geometry) prepared.set(hull, p)
  return p
}

const identityEps = 1e-5

function isIdentity(m: Matrix4): boolean {
  const e = m.elements
  for (let i = 0; i < 16; i++) if (Math.abs(e[i] - (i % 5 === 0 ? 1 : 0)) > identityEps) return false
  return true
}

/** A replaced mesh's slot and geometry variant, so a look swap can pick the right shared material. */
const SLOT_KEY = 'eclSlot'

interface SlotRecord {
  readonly slot: MaterialSlot
  readonly variant: Variant
}

function replaceSlots(lib: MaterialLibrary, instance: ModelInstance, faction: FactionId, state: ShipLookState | null): void {
  const li = internals(lib)
  const base: HullObjectData = buildHullObjectData(instance, li.data, state === null)
  instance.root.updateMatrixWorld(true)
  const assetFromWorld = new Matrix4().copy(instance.root.matrixWorld).invert()
  const assetFromMesh = new Matrix4()
  const casters = li.data.shadows.casterSlots
  instance.root.traverse((o) => {
    const mesh = o as Mesh
    if (!mesh.isMesh || Array.isArray(mesh.material)) return
    const slot = slotOf(mesh.material.name)
    if (!slot) return
    const prep = prepareGeometry(mesh.geometry, li.data.wear.featureAngleDeg)
    const variant: Variant = { baked: prep.traits.baked, trimmed: prep.traits.trimmed }
    if (slot === 'hull' && mesh.geometry !== prep.hull) mesh.geometry = prep.hull
    mesh.material = li.materialFor(slot, faction, variant)
    mesh.userData[LIVERY_KEY] = li.livery(faction)
    mesh.userData[SLOT_KEY] = { slot, variant } satisfies SlotRecord
    // Key-light shadows (environment/shadows.ts): only `shadows.casterSlots` cast (PERF PF-4: the hull); every slot receives.
    mesh.castShadow = casters.includes(slot)
    mesh.receiveShadow = true
    assetFromMesh.multiplyMatrices(assetFromWorld, mesh.matrixWorld)
    mesh.userData[HULL_DATA_KEY] = isIdentity(assetFromMesh) ? base : dataForMesh(base, assetFromMesh.clone().invert())
    if (state) mesh.userData[LOOK_STATE_KEY] = state
  })
}

export interface ShipLookOptions {
  /**
   * PERF PF-5b (D-TBD PERF-8): false = attach no light sprites; the caller draws the hull's LIGHT_ sockets from the
   * light pool instead (ship views, `materials.json lights.pooled`). Default true: one sprite per light.
   */
  readonly lightSprites?: boolean
  /**
   * PERF L-M4 part 3: true = attach only the heat shimmer per plume; the caller draws `ShipLook.plumes` from the plume
   * pool (ship views, `materials.json plumes.pooled`). Default false: per-plume shells and glow sprites.
   */
  readonly pooledPlumes?: boolean
}

const noLights = { dispose(): void {} }

export function applyShipLook(lib: MaterialLibrary, instance: ModelInstance, faction: FactionId, opts?: ShipLookOptions): ShipLook {
  const state = createLookState()
  replaceSlots(lib, instance, faction, state)
  const engines = attachEngineFx(lib, instance, faction, opts?.pooledPlumes === true)
  const lights = opts?.lightSprites === false ? noLights : attachShipLights(lib, instance)
  const shield = attachShieldFx(lib, instance)
  const clock = internals(lib).time
  return {
    plumes: engines.plumes,
    setThrust(main: number, retro: number, boost: boolean): void {
      state.thrust = main
      state.retro = retro
      state.boost = boost ? 1 : 0
      engines.setThrust(main, retro, boost)
    },
    setControl(sway: number, heave: number, pitch: number, yaw: number, roll: number): void {
      state.sway = sway
      state.heave = heave
      state.pitch = pitch
      state.yaw = yaw
      state.roll = roll
      engines.setControl(sway, heave, pitch, yaw, roll)
    },
    setDamage(hullFraction: number): void {
      state.damage = 1 - Math.min(Math.max(hullFraction, 0), 1)
    },
    hit(x: number, y: number, z: number, strength: number): void {
      void strength
      state.hullCursor = recordHit(state.hullHits, state.hullCursor, x, y, z, clock.value)
    },
    shieldHit(x: number, y: number, z: number, strength: number): void {
      state.shieldCursor = recordHit(state.shieldHits, state.shieldCursor, x, y, z, clock.value)
      shield.hit(x, y, z, strength)
    },
    dispose(): void {
      engines.dispose()
      lights.dispose()
      shield.dispose()
    },
  }
}

/**
 * Test builds only (`--mode test`): `?staticLights=sprites` draws static models' lights as sprites in the same build,
 * so the look-parity captures compare both paths on one binary. Production ignores it.
 */
const STATIC_LIGHT_SPRITES_PARAM =
  import.meta.env.MODE === 'test' && typeof location !== 'undefined' && new URLSearchParams(location.search).get('staticLights') === 'sprites'

export interface StaticLookOptions {
  /**
   * Scale W4 light-pool (D-TBD): true = the instance's LIGHT_ sockets draw from one static instanced pool
   * (`createStaticLightPool`), false = one sprite per light. Default: `materials.json lights.staticPooled`. The
   * derelict passes false (it hoists the module sprites out of its merged LOD).
   */
  readonly pooledLights?: boolean
}

/** Stations, hardpoint modules and cockpit frames: slots plus LIGHT_ sockets, no engines. Returns the lights' disposer. */
export function applyStaticLook(lib: MaterialLibrary, instance: ModelInstance, faction: FactionId, opts?: StaticLookOptions): ShipLights {
  replaceSlots(lib, instance, faction, null)
  const pooled = opts?.pooledLights ?? (internals(lib).data.lights.staticPooled && !STATIC_LIGHT_SPRITES_PARAM)
  return pooled ? (createStaticLightPool(lib, instance) ?? noLights) : attachShipLights(lib, instance)
}

/**
 * Repaints an instance already dressed by applyShipLook/applyStaticLook in another look (M6 P23): the meshes take the
 * look's packed livery and the shared slot materials stay (only a trimmed hull moving to or from first_rush switches to
 * that trim set's material). No new pipeline; not per frame. P22 calls it with `player_corp` for corporation ships
 * (setCorpLivery sets that look's colours from `CorpRecord.livery`).
 */
export function setLook(lib: MaterialLibrary, instance: ModelInstance, look: FactionId): void {
  const li = internals(lib)
  const livery = li.livery(look)
  instance.root.traverse((o) => {
    const rec = o.userData[SLOT_KEY] as SlotRecord | undefined
    if (!rec) return
    const mesh = o as Mesh
    mesh.material = li.materialFor(rec.slot, look, rec.variant)
    mesh.userData[LIVERY_KEY] = livery
  })
}
